import { OverlayTrigger, Table, Tooltip, Pagination, Modal, Button } from "react-bootstrap";
import { Check, X } from "react-feather";
import { formatNumberWithCommas } from "../../../../../utils/utility";
import { useState } from "react";

export default function List({
    writeOffs = [],
    currentPage = 1,
    perPage = 0,
    lastPage = 1, // total pages from API
    onPageChange,
    onApprove,
    onReject,
}) {
    const [showModal, setShowModal] = useState(false);
    const [selectedWriteOff, setSelectedWriteOff] = useState(null);
    const [actionType, setActionType] = useState(""); // "approve" or "reject"
    const handlePageChange = (page) => {
        if (page >= 1 && page <= lastPage) {
            onPageChange(page);
        }
    };

    const handleActionClick = (writeOff, type) => {
        setSelectedWriteOff(writeOff);
        setActionType(type);
        setShowModal(true);
    };

    const handleConfirmAction = () => {
        if (actionType === "approve" && onApprove) {
            onApprove(selectedWriteOff);
        } else if (actionType === "reject" && onReject) {
            onReject(selectedWriteOff);
        }
        setShowModal(false);
        setSelectedWriteOff(null);
        setActionType("");
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedWriteOff(null);
        setActionType("");
    };

    const renderPagination = () => {
        if (!Number.isFinite(lastPage) || lastPage <= 1) return null;

        const pageNumbers = Array.from({ length: lastPage }, (_, i) => i + 1);

        return (
            <Pagination className="justify-content-end mt-3 mb-0">
                <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                />
                {pageNumbers.map((num) => (
                    <Pagination.Item
                        key={num}
                        active={num === currentPage}
                        onClick={() => handlePageChange(num)}
                    >
                        {num}
                    </Pagination.Item>
                ))}
                <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                />
            </Pagination>
        );
    };

    // Sticky style for table headers
    const stickyHeaderStyle = {
        position: "sticky",
        top: "3rem", // adjust if top pagination height changes
        background: "#fff",
        zIndex: 2,
    };

    return (
        <div style={{ position: "relative" }}>
            {/* Top Pagination */}
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    background: "#fff",
                    zIndex: 3,
                    padding: "0.5rem 0",
                }}
            >
                {renderPagination()}
            </div>

            {/* Table */}
            <Table striped bordered hover>
                <thead className="table-light">
                    <tr>
                        {[
                            "S.No",
                            "Actions",
                            "Invoice Number",
                            "Remaining Amount",
                            "Created By",
                            "Created At",
                            "Approved By",
                            "Approved At",
                            "Status",
                        ].map((text, idx) => (
                            <th key={idx} style={stickyHeaderStyle}>
                                {text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {writeOffs?.length === 0 ? (
                        <tr>
                            <td colSpan={15} className="text-center">
                                No data available
                            </td>
                        </tr>
                    ) : (
                        writeOffs?.map((data, index) => (
                            <tr key={data.id ?? index}>
                                <td>
                                    {(currentPage - 1) * perPage + index + 1}
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Approve</Tooltip>}
                                        >
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleActionClick(data, "approve")}
                                                disabled={data.status !== "PENDING"}
                                            >
                                                <Check size={16} />
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Reject</Tooltip>}
                                        >
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleActionClick(data, "reject")}
                                                disabled={data.status !== "PENDING"}
                                            >
                                                <X size={16} />
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </td>
                                <td>
                                    {data.invoice?.id ? (
                                        <a 
                                        target="_blank"
                                        href={`/tms/invoices/payments/${data.invoice.id}`}>
                                            {data.invoice.number}
                                        </a>
                                    ) : (
                                        data.invoice?.number
                                    )}
                                </td>
                                <td>
                                    S$ {formatNumberWithCommas(data?.invoice?.remaining_amount)}
                                </td>
                                <td>
                                    {data.requestor?.name}
                                </td>
                                <td>
                                    {data.created_at ? new Date(data.created_at).toLocaleString() : "-"}
                                </td>
                                <td>
                                    {data.approver ? data?.approver?.name : "-"}
                                </td>
                                <td>
                                    {data.approver ? new Date(data.approved_at).toLocaleString() : "-"}
                                </td>
                                <td>
                                    {data.status}
                                </td>
                                
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* Bottom Pagination */}
            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#fff",
                    zIndex: 3,
                    padding: "0.5rem 0",
                }}
            >
                {renderPagination()}
            </div>

            {/* Confirmation Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to{" "}
                        <strong>{actionType === "approve" ? "approve" : "reject"}</strong>{" "}
                        this write-off request?
                    </p>
                    {selectedWriteOff && (
                        <div>
                            <strong>Invoice:</strong> {selectedWriteOff.invoice?.number}<br />
                            <strong>Amount:</strong> S$ {formatNumberWithCommas(selectedWriteOff?.invoice?.remaining_amount)}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button
                        variant={actionType === "approve" ? "success" : "danger"}
                        onClick={handleConfirmAction}
                    >
                        {actionType === "approve" ? "Approve" : "Reject"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
